import {
  GO_BOARD_GRID_LINE_INDEXES,
  GO_BOARD_MOKA_KOMI,
  GO_BOARD_MOKA_KOMI_NORMALIZATION_DIVISOR,
  GO_BOARD_MOKA_MINIMUM_PASS_MOVE_COUNT,
  GO_BOARD_MOKA_MODEL_MANIFEST_PATH,
  GO_BOARD_MOKA_MODEL_WEIGHTS_PATH,
  GO_BOARD_MODEL_WORKER_PATH,
  GO_BOARD_MOKA_INPUT_PLANE_COUNT,
  GO_BOARD_MOKA_KO_FEATURE_INDEX,
  GO_BOARD_MOKA_KOMI_FEATURE_INDEX,
  GO_BOARD_MOKA_ONE_LIBERTY_FEATURE_START_INDEX,
  GO_BOARD_MOKA_OPPONENT_STONE_FEATURE_INDEX,
  GO_BOARD_MOKA_PASS_FEATURE_START_INDEX,
  GO_BOARD_MOKA_RECENT_MOVE_COUNT,
  GO_BOARD_MOKA_RECENT_MOVE_FEATURE_START_INDEX,
  GO_BOARD_MOKA_STONE_FEATURE_INDEX,
  GO_BOARD_MOKA_TWO_LIBERTY_FEATURE_START_INDEX,
  MOKA_POLICY_PERCENTILE_SINGLE_CANDIDATE,
  MOKA_POLICY_PERCENTILE_TIE_WEIGHT,
  MOKA_ROLLOUT_POLICY_TEMPERATURE,
} from "@/constants";
import { getAdjacentGoBoardCoordinates } from "@/utils/get-adjacent-go-board-coordinates";
import { getGoBoardGroup } from "@/utils/get-go-board-group";
import { getGoBoardStones } from "@/utils/get-go-board-stones";
import { getMokaBlackWinProbabilityFromValue } from "@/utils/get-moka-black-win-probability-from-value";
import { GoModelWorkerClient } from "../../go-model/web/client";

let goModelClient: GoModelWorkerClient | null = null;
let goModelClientPromise: Promise<GoModelWorkerClient> | null = null;

const getOppositeColor = (color: "black" | "white") => (color === "black" ? "white" : "black");

const createEmptyBoard = () => {
  const boardSize = GO_BOARD_GRID_LINE_INDEXES.length;

  return Array.from({ length: boardSize }, () => Array<GoBoardStone | null>(boardSize).fill(null));
};

const createMokaMoveResult = (
  nextState: MokaGameState,
  blackWinProbability: number | null = null,
): MokaMoveResult => ({
  blackWinProbability,
  nextState,
  stones: getGoBoardStones(nextState.board),
});

const playCoordinate = (
  gameState: MokaGameState,
  coordinate: GoBoardCoordinate,
): MokaGameState | null => {
  const existingStone = gameState.board[coordinate.row]?.[coordinate.column];

  if (
    existingStone ||
    (gameState.koPoint?.column === coordinate.column && gameState.koPoint.row === coordinate.row)
  ) {
    return null;
  }

  const nextBoard = gameState.board.map((row) => [...row]);
  nextBoard[coordinate.row][coordinate.column] = {
    color: gameState.nextColor,
    ...coordinate,
  };

  const capturedCoordinates: GoBoardCoordinate[] = [];
  const oppositeColor = getOppositeColor(gameState.nextColor);

  for (const adjacentCoordinate of getAdjacentGoBoardCoordinates(coordinate, nextBoard.length)) {
    const adjacentStone = nextBoard[adjacentCoordinate.row]?.[adjacentCoordinate.column];

    if (adjacentStone?.color !== oppositeColor) {
      continue;
    }

    const adjacentGroup = getGoBoardGroup(nextBoard, adjacentCoordinate);

    if (adjacentGroup.liberties.length > 0) {
      continue;
    }

    for (const capturedCoordinate of adjacentGroup.stones) {
      nextBoard[capturedCoordinate.row][capturedCoordinate.column] = null;
      capturedCoordinates.push(capturedCoordinate);
    }
  }

  const playedGroup = getGoBoardGroup(nextBoard, coordinate);

  if (playedGroup.liberties.length === 0) {
    return null;
  }

  const isSimpleKo =
    capturedCoordinates.length === 1 &&
    playedGroup.stones.length === 1 &&
    playedGroup.liberties.length === 1;

  return {
    board: nextBoard,
    consecutivePassCount: 0,
    koPoint: isSimpleKo ? capturedCoordinates[0] : null,
    moveCount: gameState.moveCount + 1,
    moveHistory: [...gameState.moveHistory, { color: gameState.nextColor, coordinate }],
    nextColor: oppositeColor,
  };
};

const playPass = (gameState: MokaGameState): MokaGameState => ({
  ...gameState,
  consecutivePassCount: gameState.consecutivePassCount + 1,
  koPoint: null,
  moveCount: gameState.moveCount + 1,
  moveHistory: [...gameState.moveHistory, { color: gameState.nextColor, coordinate: null }],
  nextColor: getOppositeColor(gameState.nextColor),
});

const encodePosition = (gameState: MokaGameState) => {
  const boardSize = gameState.board.length;
  const boardArea = boardSize * boardSize;
  const features = new Float32Array(GO_BOARD_MOKA_INPUT_PLANE_COUNT * boardArea);
  const setFeature = (featureIndex: number, coordinate: GoBoardCoordinate, value = 1) => {
    const flattenedIndex =
      (coordinate.row * boardSize + coordinate.column) * GO_BOARD_MOKA_INPUT_PLANE_COUNT +
      featureIndex;
    features[flattenedIndex] = value;
  };

  for (const row of GO_BOARD_GRID_LINE_INDEXES) {
    for (const column of GO_BOARD_GRID_LINE_INDEXES) {
      const coordinate = { column, row };
      const stone = gameState.board[row]?.[column];

      if (!stone) {
        continue;
      }

      const isCurrentPlayer = stone.color === gameState.nextColor;
      setFeature(
        isCurrentPlayer
          ? GO_BOARD_MOKA_STONE_FEATURE_INDEX
          : GO_BOARD_MOKA_OPPONENT_STONE_FEATURE_INDEX,
        coordinate,
      );

      const libertyCount = getGoBoardGroup(gameState.board, coordinate).liberties.length;

      if (libertyCount === 1) {
        setFeature(
          GO_BOARD_MOKA_ONE_LIBERTY_FEATURE_START_INDEX + Number(!isCurrentPlayer),
          coordinate,
        );
      } else if (libertyCount === 2) {
        setFeature(
          GO_BOARD_MOKA_TWO_LIBERTY_FEATURE_START_INDEX + Number(!isCurrentPlayer),
          coordinate,
        );
      }
    }
  }

  if (gameState.koPoint) {
    setFeature(GO_BOARD_MOKA_KO_FEATURE_INDEX, gameState.koPoint);
  }

  const recentMoves = gameState.moveHistory.slice(-GO_BOARD_MOKA_RECENT_MOVE_COUNT).reverse();

  for (let recentMoveIndex = 0; recentMoveIndex < recentMoves.length; recentMoveIndex += 1) {
    const recentMove = recentMoves[recentMoveIndex];

    if (!recentMove?.coordinate) {
      for (const row of GO_BOARD_GRID_LINE_INDEXES) {
        for (const column of GO_BOARD_GRID_LINE_INDEXES) {
          setFeature(GO_BOARD_MOKA_PASS_FEATURE_START_INDEX + recentMoveIndex, { column, row });
        }
      }
      continue;
    }

    setFeature(
      GO_BOARD_MOKA_RECENT_MOVE_FEATURE_START_INDEX + recentMoveIndex,
      recentMove.coordinate,
    );
  }

  const perspectiveKomi =
    gameState.nextColor === "black" ? -GO_BOARD_MOKA_KOMI : GO_BOARD_MOKA_KOMI;

  for (const row of GO_BOARD_GRID_LINE_INDEXES) {
    for (const column of GO_BOARD_GRID_LINE_INDEXES) {
      setFeature(
        GO_BOARD_MOKA_KOMI_FEATURE_INDEX,
        { column, row },
        perspectiveKomi / GO_BOARD_MOKA_KOMI_NORMALIZATION_DIVISOR,
      );
    }
  }

  return features;
};

const getGoModelClient = async (
  onProgress?: (loadedByteCount: number, totalByteCount: number) => void,
) => {
  if (goModelClient) {
    return goModelClient;
  }

  if (!goModelClientPromise) {
    const client = new GoModelWorkerClient(
      new Worker(GO_BOARD_MODEL_WORKER_PATH, { type: "module" }),
    );
    goModelClientPromise = client
      .initialize({
        isGzipCompressed: true,
        manifestUrl: GO_BOARD_MOKA_MODEL_MANIFEST_PATH,
        onProgress,
        weightsUrl: GO_BOARD_MOKA_MODEL_WEIGHTS_PATH,
      })
      .then(() => client);
  }

  goModelClient = await goModelClientPromise;
  return goModelClient;
};

const createMokaGameState = (): MokaGameState => ({
  board: createEmptyBoard(),
  consecutivePassCount: 0,
  koPoint: null,
  moveCount: 0,
  moveHistory: [],
  nextColor: "black",
});

const initializeMokaEngine = async (
  onProgress?: (loadedByteCount: number, totalByteCount: number) => void,
) => {
  await getGoModelClient(onProgress);
};

const getMokaBlackWinProbability = async (gameState: MokaGameState) => {
  const client = await getGoModelClient();
  const inference = await client.infer(encodePosition(gameState));

  return getMokaBlackWinProbabilityFromValue(gameState, inference.value);
};

const getMokaSymmetryBlackWinProbability = async (gameState: MokaGameState) => {
  const client = await getGoModelClient();
  const { evaluateMokaSymmetries } = await import("@/lib/moka-symmetry-evaluation");
  const inference = await evaluateMokaSymmetries(encodePosition(gameState), (symmetryFeatures) =>
    client.inferBatch(symmetryFeatures),
  );

  return getMokaBlackWinProbabilityFromValue(gameState, inference.value);
};

const playMokaCoordinate = (
  gameState: MokaGameState,
  coordinate: GoBoardCoordinate,
): MokaMoveResult | null => {
  const nextState = playCoordinate(gameState, coordinate);

  return nextState ? createMokaMoveResult(nextState) : null;
};

const passMokaTurn = (gameState: MokaGameState): MokaMoveResult =>
  createMokaMoveResult(playPass(gameState));

const getMokaMoveCandidates = (gameState: MokaGameState, policyData: Float32Array) => {
  const boardSize = gameState.board.length;
  const passMove = boardSize * boardSize;
  const candidates: MokaMoveCandidate[] =
    gameState.moveCount >= GO_BOARD_MOKA_MINIMUM_PASS_MOVE_COUNT
      ? [{ coordinate: null, policyValue: policyData[passMove], state: playPass(gameState) }]
      : [];

  for (const row of GO_BOARD_GRID_LINE_INDEXES) {
    for (const column of GO_BOARD_GRID_LINE_INDEXES) {
      const coordinate = { column, row };
      const policyValue = policyData[row * boardSize + column];
      const candidateState = playCoordinate(gameState, coordinate);

      if (!candidateState) {
        continue;
      }

      candidates.push({ coordinate, policyValue, state: candidateState });
    }
  }

  return candidates;
};

const getMokaCandidatePolicyPercentile = (
  candidates: MokaMoveCandidate[],
  selectedCandidate: MokaMoveCandidate,
) => {
  if (candidates.length === MOKA_POLICY_PERCENTILE_SINGLE_CANDIDATE) {
    return MOKA_POLICY_PERCENTILE_SINGLE_CANDIDATE;
  }

  const lowerPolicyValueCount = candidates.filter(
    (candidate) => candidate.policyValue < selectedCandidate.policyValue,
  ).length;
  const equalPolicyValueCount = candidates.filter(
    (candidate) => candidate.policyValue === selectedCandidate.policyValue,
  ).length;
  const tiedAlternativeCount = equalPolicyValueCount - MOKA_POLICY_PERCENTILE_SINGLE_CANDIDATE;
  const candidateAlternativeCount = candidates.length - MOKA_POLICY_PERCENTILE_SINGLE_CANDIDATE;

  return (
    (lowerPolicyValueCount + tiedAlternativeCount * MOKA_POLICY_PERCENTILE_TIE_WEIGHT) /
    candidateAlternativeCount
  );
};

const getMokaSearchOptions = (
  client: GoModelWorkerClient,
  evaluateRootState: MokaSearchOptions["evaluateRootState"],
): MokaSearchOptions => ({
  evaluateRootState,
  evaluateStates: (gameStates) =>
    client.inferBatch(gameStates.map((candidateGameState) => encodePosition(candidateGameState))),
  getMoveCandidates: getMokaMoveCandidates,
});

const getMokaMoveAnalyses = async (gameState: MokaGameState) => {
  const client = await getGoModelClient();
  const [{ getMokaSearchMoveAnalyses }, { evaluateMokaSymmetries }] = await Promise.all([
    import("@/lib/moka-search"),
    import("@/lib/moka-symmetry-evaluation"),
  ]);

  return getMokaSearchMoveAnalyses(
    gameState,
    getMokaSearchOptions(client, (rootGameState) =>
      evaluateMokaSymmetries(encodePosition(rootGameState), (symmetryFeatures) =>
        client.inferBatch(symmetryFeatures),
      ),
    ),
  );
};

const getMokaMovePolicyPercentile = async (
  gameState: MokaGameState,
  coordinate: GoBoardCoordinate,
) => {
  const client = await getGoModelClient();
  const inference = await client.infer(encodePosition(gameState));
  const candidates = getMokaMoveCandidates(gameState, inference.policyLogits);
  const selectedCandidate = candidates.find(
    (candidate) =>
      candidate.coordinate?.column === coordinate.column &&
      candidate.coordinate.row === coordinate.row,
  );

  if (!selectedCandidate) {
    return null;
  }

  return getMokaCandidatePolicyPercentile(candidates, selectedCandidate);
};

const playMokaMove = async (
  gameState: MokaGameState,
  randomSelectionValue?: number,
): Promise<MokaMoveResult> => {
  const client = await getGoModelClient();

  if (randomSelectionValue === undefined) {
    const [{ selectMokaSearchMove }, { evaluateMokaSymmetries }] = await Promise.all([
      import("@/lib/moka-search"),
      import("@/lib/moka-symmetry-evaluation"),
    ]);
    const searchSelection = await selectMokaSearchMove(
      gameState,
      getMokaSearchOptions(client, (rootGameState) =>
        evaluateMokaSymmetries(encodePosition(rootGameState), (symmetryFeatures) =>
          client.inferBatch(symmetryFeatures),
        ),
      ),
    );

    return createMokaMoveResult(
      searchSelection.moveCandidate?.state ?? playPass(gameState),
      searchSelection.blackWinProbability,
    );
  }

  const inference = await client.infer(encodePosition(gameState));
  const candidates = getMokaMoveCandidates(gameState, inference.policyLogits);

  let selectedCandidate = candidates[0];

  if (selectedCandidate) {
    const maximumPolicyValue = Math.max(...candidates.map((candidate) => candidate.policyValue));
    const candidateWeights = candidates.map((candidate) =>
      Math.exp((candidate.policyValue - maximumPolicyValue) / MOKA_ROLLOUT_POLICY_TEMPERATURE),
    );
    const totalCandidateWeight = candidateWeights.reduce(
      (totalWeight, candidateWeight) => totalWeight + candidateWeight,
      0,
    );
    const selectedWeight = randomSelectionValue * totalCandidateWeight;
    let cumulativeWeight = 0;

    for (let candidateIndex = 0; candidateIndex < candidates.length; candidateIndex += 1) {
      cumulativeWeight += candidateWeights[candidateIndex];

      if (selectedWeight <= cumulativeWeight) {
        selectedCandidate = candidates[candidateIndex];
        break;
      }
    }
  }

  const nextState = selectedCandidate?.state ?? playPass(gameState);

  return createMokaMoveResult(nextState);
};

export {
  createMokaGameState,
  getMokaBlackWinProbability,
  getMokaSymmetryBlackWinProbability,
  getMokaMoveAnalyses,
  getMokaMovePolicyPercentile,
  initializeMokaEngine,
  passMokaTurn,
  playMokaCoordinate,
  playMokaMove,
};
